import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Button, Card, Modal, Select, Textarea } from '@helix-ds/react'
import { ArrowLeft, ThumbsUp, ThumbsDown, TrashBin, PenNib, Lock, UserAdd } from 'flowbite-react-icons/outline'
import { format as timeagoFormat } from 'timeago.js'
import { Navbar } from '@/components/Navbar'
import { getClientName } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useLaunchMode } from '@/features/auth/launchContext'
import { useAuthorization } from '@/features/authorization/authorizationHooks'
import { PermissionGuard } from '@/features/authorization/PermissionGuard'
import {
  addSuggestion,
  castVote,
  CaseConnectError,
  closeCase,
  createCaseForOrder,
  editDiscussion,
  getCase,
  getCaseByOrder,
  inviteMembers,
  listDiscussion,
  listMembers,
  listSuggestions,
  listVoteItems,
  postDiscussion,
  removeMember,
} from '@/features/caseConnect/services/caseConnectClient'
import {
  CaseDetail,
  CaseMember,
  CaseSuggestion,
  CaseVoteItem,
  CaseVoteTally,
  DiscussionMessage,
} from '@/features/caseConnect/types'
import { listCaseInvitations, revokeInvitation } from '@/features/invitations'
import type { Invitation } from '@/features/invitations'

// helix-exception: dense badges keep 10-11px text; Helix has no step below text-xs (F-12)

const HEADING = 'text-text-heading dark:text-white font-semibold tracking-tight'
const SUBTLE = 'text-sm font-medium text-text-body-subtle dark:text-gray-500'
const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wide text-text-body-subtle dark:text-gray-500'

const ROLE_OPTIONS = [
  { value: 'case_manager', label: 'Case Manager' },
  { value: 'case_writer', label: 'Case Writer' },
  { value: 'case_read_only', label: 'Case Read Only' },
]

function useCaller(): string {
  const { user } = useAuth0()
  return user?.email || user?.sub || 'unknown-user'
}

/** Two-letter initials from a display name / identifier (defensive; '?' when empty). */
function initialsOf(name: string | null | undefined): string {
  if (!name || !name.trim()) return '?'
  const parts = name.trim().split(/[\s._@-]+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase().slice(0, 2) || '?'
}

/** Neutral author label shown when the server did not resolve a display name.
 * NEVER the raw user id or email (avoids surfacing an identifier). */
const NEUTRAL_AUTHOR = 'Participant'

/** Parse an ISO/date string into a valid Date, or null when absent/unparseable. */
function toValidDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Relative "timeago" label from a createdAt string; '' when absent/unparseable.
 * The relative time is derived client-side (never fabricated by the server). */
function relativeTime(value: string | null | undefined): string {
  const d = toValidDate(value)
  return d ? timeagoFormat(d) : ''
}

/** Absolute, human-readable timestamp for the time tooltip; '' when absent. */
function absoluteTime(value: string | null | undefined): string {
  const d = toValidDate(value)
  return d ? d.toLocaleString() : ''
}

/** Humanize a snake_case / camelCase key into a readable label. */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Group vote items by itemType, preserving first-seen type order. */
function groupVoteItemsByType(items: CaseVoteItem[]): [string, CaseVoteItem[]][] {
  const groups = new Map<string, CaseVoteItem[]>()
  for (const item of items) {
    const key = item.itemType || 'OTHER'
    const bucket = groups.get(key)
    if (bucket) bucket.push(item)
    else groups.set(key, [item])
  }
  return Array.from(groups.entries())
}

/** Render a single anonymized value: primitives as-is, everything else as JSON (defensive fallback). */
function renderScalar(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value)
}

type AnonymizedVariant = {
  gene?: unknown
  variant?: unknown
  zygosity?: unknown
  classification?: unknown
}

/** One normalized guidance condition (matches the API's anonymizedSummary.conditions[]). */
type AnonymizedCondition = {
  condition?: unknown
  phenotypeSummary?: unknown
  gene?: unknown
  inheritance?: unknown
  carePlan?: unknown
  warnings?: unknown
  keyFindings?: unknown
}

/** True when the summary carries a non-empty variants array we can render as chips. */
function hasVariantList(summary: Record<string, unknown>): summary is Record<string, unknown> & {
  variants: AnonymizedVariant[]
} {
  return Array.isArray(summary['variants']) && summary['variants'].length > 0
}

/** First present string value among the given summary keys (defensive; PHI-safe: summary only). */
function firstStringField(summary: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = summary[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/** Coerce a summary field into a list of strings (array of strings/objects or delimited string). */
function stringListField(summary: Record<string, unknown>, keys: string[]): string[] {
  for (const key of keys) {
    const value = summary[key]
    if (Array.isArray(value) && value.length > 0) {
      return value
        .map((entry) => {
          if (typeof entry === 'string' || typeof entry === 'number') return String(entry)
          if (entry && typeof entry === 'object') {
            const rec = entry as Record<string, unknown>
            const label = rec['label'] ?? rec['name'] ?? rec['title'] ?? rec['item'] ?? rec['text']
            return typeof label === 'string' ? label : JSON.stringify(entry)
          }
          return String(entry)
        })
        .filter(Boolean)
    }
  }
  return []
}

/** True when the summary carries a non-empty normalized conditions[] array. */
function hasConditionList(summary: Record<string, unknown>): summary is Record<string, unknown> & {
  conditions: AnonymizedCondition[]
} {
  return Array.isArray(summary['conditions']) && summary['conditions'].length > 0
}

/** Care-plan action texts from a condition's carePlan[] ([{type,text}] or string list). */
function carePlanTexts(carePlan: unknown): string[] {
  if (!Array.isArray(carePlan)) return []
  return carePlan
    .map((entry) => {
      if (typeof entry === 'string') return entry
      if (entry && typeof entry === 'object') {
        const text = (entry as Record<string, unknown>)['text']
        return typeof text === 'string' ? text : ''
      }
      return ''
    })
    .filter(Boolean)
}

/** Scrubbed string list from a condition field (warnings / keyFindings). */
function conditionStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
}

/** First non-empty string among a condition's keys (defensive). */
function conditionString(condition: AnonymizedCondition, keys: (keyof AnonymizedCondition)[]): string | null {
  for (const key of keys) {
    const value = condition[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/** Chip: gene name + classification pill (e.g. "MOCS1  Pathogenic"). */
function VariantChips({ variants }: { variants: AnonymizedVariant[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {variants.map((v, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-2 rounded-full border border-border-base dark:border-gray-700 px-3 py-1 text-xs font-medium text-text-body dark:text-gray-400"
        >
          <span className="font-semibold text-text-heading dark:text-white">
            {renderScalar(v.gene) || 'Unknown gene'}
          </span>
          {v.classification != null && (
            <span className="rounded-full bg-error-100 dark:bg-gray-900 px-2 py-0.5 font-semibold text-text-fg-error dark:text-error-100">
              {renderScalar(v.classification)}
            </span>
          )}
          {v.zygosity != null && (
            <span className="text-text-body-subtle dark:text-gray-500">{renderScalar(v.zygosity)}</span>
          )}
          {v.variant != null && (
            <span className="text-text-body-subtle dark:text-gray-500">{renderScalar(v.variant)}</span>
          )}
        </span>
      ))}
    </div>
  )
}

/** Key/value block for any remaining (unknown-shape) summary fields — defensive fallback. */
function SummaryFields({ entries }: { entries: [string, unknown][] }) {
  if (entries.length === 0) return null
  return (
    <dl className="flex flex-col gap-3">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-col gap-1">
          <dt className={SECTION_LABEL}>{humanizeKey(key)}</dt>
          <dd className="text-sm font-medium text-text-body dark:text-gray-400">{renderScalar(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

// Summary keys the structured sections consume; the rest fall through to the
// defensive key/value block so no anonymizedSummary field is silently dropped.
const PHENOTYPE_KEYS = ['phenotypeSummary', 'phenotype_summary', 'phenotype', 'summary', 'narrative', 'description']
const CARE_PLAN_KEYS = ['carePlan', 'care_plan', 'carePlanItems', 'care_plan_items', 'careItems']
const CONDITION_KEYS = ['condition', 'conditions', 'finding', 'findings', 'diagnosis']
const STRUCTURED_KEYS = new Set([...PHENOTYPE_KEYS, ...CARE_PLAN_KEYS, 'variants', 'conditions', 'status'])

/** One normalized condition: PHENOTYPE SUMMARY + CARE PLAN SUMMARY + warnings. */
function ConditionSection({ condition }: { condition: AnonymizedCondition }) {
  const name = conditionString(condition, ['condition'])
  const gene = conditionString(condition, ['gene'])
  const inheritance = conditionString(condition, ['inheritance'])
  const phenotypeSummary = conditionString(condition, ['phenotypeSummary'])
  const carePlan = carePlanTexts(condition.carePlan)
  const warnings = conditionStringList(condition.warnings)
  return (
    <section className="flex flex-col gap-4 rounded-base border border-border-base dark:border-gray-700 p-4">
      {(name || gene) && (
        <div className="flex flex-wrap items-baseline gap-2">
          {name && <h3 className={`${HEADING} text-base`}>{name}</h3>}
          {gene && (
            <span className="rounded-full border border-border-base dark:border-gray-700 px-2 py-0.5 text-xs font-medium text-text-body-subtle dark:text-gray-500">
              {gene}
              {inheritance ? ` · ${inheritance}` : ''}
            </span>
          )}
        </div>
      )}
      {phenotypeSummary && (
        <div className="rounded-base bg-background-gray dark:bg-gray-800 p-4">
          <h4 className={SECTION_LABEL}>Phenotype summary</h4>
          <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">{phenotypeSummary}</p>
        </div>
      )}
      {carePlan.length > 0 && (
        <div>
          <h4 className={SECTION_LABEL}>Care plan summary</h4>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm font-medium text-text-body dark:text-gray-400">
            {carePlan.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-text-fg-error dark:text-error-100">Warnings</h4>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm font-medium text-text-fg-error-strong dark:text-error-100">
            {warnings.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/**
 * Anonymized report summary card. Renders ONLY case.anonymizedSummary (PHI
 * already stripped by API). Mirrors the mockup: a "Prototype finding summary"
 * subheading, variant chips (gene + classification pill), then per-condition
 * PHENOTYPE SUMMARY + CARE PLAN SUMMARY sections (from the normalized
 * anonymizedSummary.conditions[]) plus any warnings. When the summary has no
 * conditions[] (unknown/legacy shape) it falls back to reading the flat
 * phenotype/care-plan keys, and any field not consumed by a structured section
 * falls through to a humanized key/value block — so unknown shapes still render
 * and nothing outside anonymizedSummary is shown.
 */
function AnonymizedReportCard({ detail }: { detail: CaseDetail }) {
  const summary = detail.case.anonymizedSummary ?? {}
  const variants = hasVariantList(summary) ? summary.variants : null
  const conditions = hasConditionList(summary) ? summary.conditions : null
  // Legacy / flat fallback (only when no normalized conditions[] present).
  const flatPhenotype = conditions ? null : firstStringField(summary, PHENOTYPE_KEYS)
  const flatCarePlan = conditions ? [] : stringListField(summary, CARE_PLAN_KEYS)
  const otherEntries = Object.entries(summary).filter(
    ([key]) => !STRUCTURED_KEYS.has(key) && !CONDITION_KEYS.includes(key)
  )
  const isEmpty =
    !variants && !conditions && !flatPhenotype && flatCarePlan.length === 0 && otherEntries.length === 0
  return (
    <Card aria-labelledby="cc-report-heading">
      <h2 id="cc-report-heading" className={`${HEADING} text-lg`}>
        Anonymized Report
      </h2>
      <p className="mt-1 text-xs font-medium text-text-body-subtle dark:text-gray-500">
        This summary is anonymized — no patient identifiers are shown or stored.
      </p>
      {isEmpty ? (
        <p className={`mt-4 ${SUBTLE}`}>No anonymized findings available.</p>
      ) : (
        <div className="mt-4 flex flex-col gap-5">
          <p className="text-sm font-medium text-text-body dark:text-gray-400">Prototype finding summary</p>
          {variants && <VariantChips variants={variants} />}
          {conditions &&
            conditions.map((condition, i) => <ConditionSection key={i} condition={condition} />)}
          {flatPhenotype && (
            <section className="rounded-base bg-background-gray dark:bg-gray-800 p-4">
              <h3 className={SECTION_LABEL}>Phenotype summary</h3>
              <p className="mt-2 text-sm font-medium text-text-body dark:text-gray-400">{flatPhenotype}</p>
            </section>
          )}
          {flatCarePlan.length > 0 && (
            <section>
              <h3 className={SECTION_LABEL}>Care plan summary</h3>
              <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm font-medium text-text-body dark:text-gray-400">
                {flatCarePlan.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </section>
          )}
          <SummaryFields entries={otherEntries} />
        </div>
      )}
    </Card>
  )
}

/** Read a human-readable condition/finding label from the anonymized summary (best-effort). */
function summaryConditionLabel(summary: Record<string, unknown> | null | undefined): string | null {
  if (!summary) return null
  if (hasConditionList(summary)) {
    const first = conditionString(summary.conditions[0], ['condition'])
    if (first) return first
  }
  return firstStringField(summary, CONDITION_KEYS)
}

/** Care plan voting card (per-item thumbs up/down, with category label per row). */
/**
 * A shared UP/DOWN vote-button pair with live tally + pressed state. Used by
 * both the Care Plan Voting items and the Alternative Suggestions so the two
 * render identically.
 */
function VoteButtons({
  label,
  tally,
  myVote,
  disabled,
  onVote,
}: {
  label: string
  tally: CaseVoteTally
  myVote?: 'UP' | 'DOWN' | null
  disabled: boolean
  onVote: (value: 'UP' | 'DOWN') => void
}) {
  const upActive = myVote === 'UP'
  const downActive = myVote === 'DOWN'
  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        aria-label={`Vote up on ${label}`}
        aria-pressed={upActive}
        disabled={disabled}
        onClick={() => onVote('UP')}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium focus:ring focus:ring-brand-600 disabled:opacity-50 ${
          upActive
            ? 'border-brand-600 dark:border-brand-500 bg-brand-600 text-white'
            : 'border-border-base dark:border-gray-700 text-text-body dark:text-gray-400 hover:bg-brand-600 hover:text-white'
        }`}
      >
        <ThumbsUp className="h-4 w-4" />
        {tally.up}
      </button>
      <button
        type="button"
        aria-label={`Vote down on ${label}`}
        aria-pressed={downActive}
        disabled={disabled}
        onClick={() => onVote('DOWN')}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium focus:ring focus:ring-brand-600 disabled:opacity-50 ${
          downActive
            ? 'border-brand-600 dark:border-brand-500 bg-brand-600 text-white'
            : 'border-border-base dark:border-gray-700 text-text-body dark:text-gray-400 hover:bg-brand-600 hover:text-white'
        }`}
      >
        <ThumbsDown className="h-4 w-4" />
        {tally.down}
      </button>
    </div>
  )
}

function VotingCard({
  items,
  canVote,
  disabled,
  onVote,
  canViewOrder,
  orderId,
}: {
  items: CaseVoteItem[]
  canVote: boolean
  disabled: boolean
  onVote: (voteItemId: string, value: 'UP' | 'DOWN') => void
  canViewOrder: boolean
  orderId?: string
}) {
  const votingDisabled = !canVote || disabled
  return (
    <Card aria-labelledby="cc-vote-heading">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 id="cc-vote-heading" className={`${HEADING} text-lg`}>
            Care Plan Voting
          </h2>
          <p className="mt-1 text-xs font-medium text-text-body-subtle dark:text-gray-500">
            Suggested actions from guidance. Use discussion and votes to decide what belongs on the patient&apos;s care
            plan.
          </p>
        </div>
        {canViewOrder && orderId ? (
          <Link
            to={`/order/guidance/${orderId}`}
            className="shrink-0 rounded-full border border-border-base dark:border-gray-700 px-3 py-1 text-xs font-medium text-text-body dark:text-gray-400 transition-colors hover:bg-brand-600 hover:text-white focus:ring focus:ring-brand-600"
          >
            Open case
          </Link>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className={`mt-4 ${SUBTLE}`}>No items to vote on yet.</p>
      ) : (
        <div className="mt-4 flex flex-col gap-5">
          {groupVoteItemsByType(items).map(([itemType, groupItems]) => (
            <section key={itemType} aria-label={humanizeKey(itemType)}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-400">
                {humanizeKey(itemType)}
              </h3>
              <ul className="mt-2 flex flex-col gap-2.5">
                {groupItems.map((item) => {
                  return (
                    <li
                      key={item.voteItemId}
                      className="flex items-center justify-between gap-3 rounded-base border border-border-base dark:border-gray-700 p-3"
                    >
                      <span className="text-sm font-medium text-text-body dark:text-gray-400">{item.label}</span>
                      <VoteButtons
                        label={item.label}
                        tally={item.tally}
                        myVote={item.myVote}
                        disabled={votingDisabled}
                        onVote={(value) => onVote(item.voteItemId, value)}
                      />
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Card>
  )
}

/** Alternative suggestions card (free-text). A "Suggest" button reveals the compose form. */
function SuggestionsCard({
  suggestions,
  voteItems,
  canSuggest,
  canVote,
  disabled,
  onAdd,
  onVote,
}: {
  suggestions: CaseSuggestion[]
  /** SUGGESTION-source vote items, joined to suggestions by suggestionId. */
  voteItems: CaseVoteItem[]
  canSuggest: boolean
  canVote: boolean
  disabled: boolean
  onAdd: (body: string, rationale: string) => Promise<boolean>
  onVote: (voteItemId: string, value: 'UP' | 'DOWN') => void
}) {
  const [body, setBody] = useState('')
  const [rationale, setRationale] = useState('')
  const [composing, setComposing] = useState(false)
  const canCompose = canSuggest && !disabled
  const votingDisabled = !canVote || disabled
  // Join each suggestion to its vote item by suggestionId so the free-text
  // suggestion and its votable counterpart render as one row.
  const voteBySuggestionId = new Map<string, CaseVoteItem>()
  for (const v of voteItems) {
    if (v.source === 'SUGGESTION' && v.suggestionId) voteBySuggestionId.set(v.suggestionId, v)
  }
  return (
    <Card aria-labelledby="cc-suggest-heading">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 id="cc-suggest-heading" className={`${HEADING} text-lg`}>
            Alternative Suggestions
          </h2>
          <p className="mt-1 text-xs font-medium text-text-body-subtle dark:text-gray-500">
            Propose a different action or a different sequencing strategy for the team.
          </p>
        </div>
        {canCompose && !composing && (
          <Button type="button" variant="secondary" onClick={() => setComposing(true)}>
            Suggest
          </Button>
        )}
      </div>
      {canCompose && composing && (
        <div className="mt-4 flex flex-col gap-2.5">
          <Textarea
            id="cc-suggest-body"
            label="Describe your alternative suggestion"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
          />
          <Textarea
            id="cc-suggest-rationale"
            label="Rationale (optional)"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
          />
          <div className="flex gap-2 self-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setComposing(false)
                setBody('')
                setRationale('')
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!body.trim()}
              onClick={() => {
                // Clear inputs (and collapse the form) ONLY after the add succeeds (SHOULD-FIX #3).
                void onAdd(body.trim(), rationale.trim()).then((ok) => {
                  if (ok) {
                    setBody('')
                    setRationale('')
                    setComposing(false)
                  }
                })
              }}
            >
              Submit suggestion
            </Button>
          </div>
        </div>
      )}
      {suggestions.length === 0 ? (
        <p className={`mt-4 ${SUBTLE}`}>No suggestions yet.</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2.5">
          {suggestions.map((s) => {
            const vote = voteBySuggestionId.get(s.suggestionId)
            return (
              <li
                key={s.suggestionId}
                className="flex items-start justify-between gap-3 rounded-base border border-border-base dark:border-gray-700 p-3"
              >
                <div className="min-w-0">
                  {s.authorName && (
                    <p className="text-xs font-semibold text-text-heading dark:text-white">{s.authorName}</p>
                  )}
                  <p className="mt-0.5 text-sm font-medium text-text-body dark:text-gray-400">{s.body}</p>
                  {s.rationale && (
                    <p className="mt-1 text-xs font-medium text-text-body-subtle dark:text-gray-500">
                      Rationale: {s.rationale}
                    </p>
                  )}
                </div>
                {vote && (
                  <VoteButtons
                    label={s.body}
                    tally={vote.tally}
                    myVote={vote.myVote}
                    disabled={votingDisabled}
                    onVote={(value) => onVote(vote.voteItemId, value)}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

/** One discussion message rendered as a chat bubble (own = right/brand, other = left/gray). */
function MessageBubble({
  message,
  canCompose,
  editingId,
  editBody,
  onStartEdit,
  onChangeEdit,
  onCancelEdit,
  onSaveEdit,
}: {
  message: DiscussionMessage
  canCompose: boolean
  editingId: string | null
  editBody: string
  onStartEdit: (id: string, body: string) => void
  onChangeEdit: (body: string) => void
  onCancelEdit: () => void
  onSaveEdit: (id: string) => void
}) {
  const m = message
  // Ownership for alignment uses the server-provided `isMine` when available,
  // else falls back to `canEdit` (the only other server-computed "own" signal).
  // The SPA never compares ids to decide ownership (DEFECT #1 / #4).
  const isMine = m.isMine ?? m.canEdit === true
  const canEditThis = m.canEdit === true
  // Relative "timeago" label + absolute tooltip; hidden entirely when createdAt
  // is missing/unparseable.
  const relTime = relativeTime(m.createdAt)
  const absTime = absoluteTime(m.createdAt)
  // Display name is the server-resolved clinician name; fall back to a NEUTRAL
  // label (never the raw user id or email) when the server did not resolve one.
  const displayName = m.authorName?.trim() || NEUTRAL_AUTHOR
  const initials = initialsOf(m.authorName)
  const isEditing = editingId === m.discussionId

  if (isEditing) {
    return (
      <li className="rounded-base border border-border-base dark:border-gray-700 p-3">
        <div className="flex flex-col gap-2">
          <Textarea value={editBody} onChange={(e) => onChangeEdit(e.target.value)} rows={2} aria-label="Edit message" />
          <div className="flex gap-2 self-end">
            <Button type="button" variant="secondary" size="sm" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={!editBody.trim()}
              onClick={() => onSaveEdit(m.discussionId)}
            >
              Save
            </Button>
          </div>
        </div>
      </li>
    )
  }

  return (
    <li className={`flex flex-col gap-1 ${isMine ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-center gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
        {!isMine && (
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-gray-800 text-[10px] font-semibold text-brand-700 dark:text-brand-400"
            aria-hidden
          >
            {initials}
          </span>
        )}
        <span className="text-xs font-semibold text-text-heading dark:text-white">{displayName}</span>
        {relTime && (
          <time
            dateTime={m.createdAt ?? undefined}
            title={absTime}
            className="text-xs font-medium text-text-body-subtle dark:text-gray-500"
          >
            {relTime}
          </time>
        )}
      </div>
      <div
        className={`max-w-[85%] rounded-base px-3 py-2 text-sm font-medium ${
          isMine
            ? 'bg-brand-600 text-white'
            : 'bg-background-gray dark:bg-gray-800 text-text-body dark:text-gray-400'
        }`}
      >
        {m.body}
        {m.editedAt && (
          <span className={`ml-2 text-xs ${isMine ? 'text-white/70' : 'text-text-body-subtle dark:text-gray-500'}`}>
            (edited)
          </span>
        )}
      </div>
      <div className={`flex items-center gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
        <span className="text-[10px] font-medium text-text-body-subtle dark:text-gray-500">Delivered</span>
        {canEditThis && canCompose && (
          <button
            type="button"
            aria-label="Edit your message"
            onClick={() => onStartEdit(m.discussionId, m.body)}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border-base dark:border-gray-700 px-2 py-0.5 text-[10px] font-medium text-text-body dark:text-gray-400 hover:bg-brand-600 hover:text-white"
          >
            <PenNib className="h-3 w-3" />
            Edit
          </button>
        )}
      </div>
    </li>
  )
}

/** Discussion thread panel (edit-own). Tall side panel: header + scrollable
 * chat-bubble list + compose box pinned at the bottom. */
function DiscussionPanel({
  messages,
  canDiscuss,
  disabled,
  onPost,
  onEdit,
}: {
  messages: DiscussionMessage[]
  canDiscuss: boolean
  disabled: boolean
  onPost: (body: string) => Promise<boolean>
  onEdit: (discussionId: string, body: string) => Promise<boolean>
}) {
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const canCompose = canDiscuss && !disabled
  return (
    <Card
      aria-labelledby="cc-discussion-heading"
      className="flex h-full max-h-[calc(100vh-12rem)] flex-col lg:sticky lg:top-6"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="cc-discussion-heading" className={`${HEADING} text-lg`}>
          Discussion
        </h2>
        <span className="text-xs font-medium text-text-body-subtle dark:text-gray-500">
          {messages.length} {messages.length === 1 ? 'message' : 'messages'}
        </span>
      </div>
      <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <p className={SUBTLE}>No messages yet.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {messages.map((m) => (
              <MessageBubble
                key={m.discussionId}
                message={m}
                canCompose={canCompose}
                editingId={editingId}
                editBody={editBody}
                onStartEdit={(id, body) => {
                  setEditingId(id)
                  setEditBody(body)
                }}
                onChangeEdit={setEditBody}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={(id) => {
                  // Close the editor ONLY after the edit succeeds; on failure
                  // keep the editor open with the user's text.
                  void onEdit(id, editBody.trim()).then((ok) => {
                    if (ok) setEditingId(null)
                  })
                }}
              />
            ))}
          </ul>
        )}
      </div>
      {canCompose && (
        <div className="mt-4 flex items-end gap-2 border-t border-border-base dark:border-gray-700 pt-4">
          <div className="flex-1">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type a message..."
              aria-label="New message"
              rows={2}
            />
          </div>
          <Button
            type="button"
            variant="primary"
            disabled={!draft.trim()}
            onClick={() => {
              // Clear the compose box ONLY after the post succeeds; on failure
              // keep the draft so the user does not lose their text (SHOULD-FIX #3).
              void onPost(draft.trim()).then((ok) => {
                if (ok) setDraft('')
              })
            }}
          >
            Send
          </Button>
        </div>
      )}
    </Card>
  )
}

/** Manage access modal (invite emails + pending invitations + current membership). */
function ManageAccessModal({
  members,
  invitations,
  disabled,
  inviteResult,
  onInvite,
  onRevoke,
  onRemove,
  onClose,
}: {
  members: CaseMember[]
  invitations: Invitation[]
  disabled: boolean
  inviteResult: { sentCount: number; failed: string[] } | null
  onInvite: (emails: string[], roleKey: string) => Promise<boolean>
  onRevoke: (invitationId: string) => void
  onRemove: (userId: string) => void
  onClose: () => void
}) {
  const [emailText, setEmailText] = useState('')
  const [roleKey, setRoleKey] = useState('case_writer')
  const parsedEmails = emailText
    .split(/[\n,\s]+/)
    .map((e) => e.trim())
    .filter(Boolean)
  const canSubmit = parsedEmails.length > 0 && !disabled
  // Outstanding invites (awaiting the recipient's accept) surface with a Revoke
  // action. Accepted invitations are intentionally not shown here — the invitee
  // already appears in "Current participants" below, so listing them again as
  // "joined" is redundant.
  const pendingInvitations = invitations.filter((inv) => inv.status === 'PENDING')
  return (
    <Modal isOpen onClose={onClose} title="Manage Access" size="lg">
      <div className="flex flex-col gap-2.5">
        <Textarea
          id="cc-invite-emails"
          label="Invite by email (comma, space, or newline separated)"
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
          rows={3}
          disabled={disabled}
        />
        <Select
          id="cc-invite-role"
          label="Role"
          value={roleKey}
          onChange={(e) => setRoleKey(e.target.value)}
          options={ROLE_OPTIONS}
          disabled={disabled}
        />
        <Button
          type="button"
          variant="primary"
          className="self-end"
          disabled={!canSubmit}
          onClick={() => {
            // Clear the email box ONLY after invites succeed (SHOULD-FIX #3).
            void onInvite(parsedEmails, roleKey).then((ok) => {
              if (ok) setEmailText('')
            })
          }}
        >
          Send invites
        </Button>
        {parsedEmails.length === 0 && emailText.trim().length > 0 && (
          <p className="text-xs font-medium text-text-fg-error dark:text-error-100" role="alert">
            Enter at least one valid email address.
          </p>
        )}
        {inviteResult && (
          <div className="rounded-base border border-border-base dark:border-gray-700 p-3 text-xs font-medium">
            {inviteResult.sentCount > 0 && (
              <p className="text-text-body dark:text-gray-400">
                Invitation sent to {inviteResult.sentCount} recipient{inviteResult.sentCount === 1 ? '' : 's'}.
              </p>
            )}
            {inviteResult.failed.length > 0 && (
              <p className="mt-1 text-text-fg-error dark:text-error-100">Failed: {inviteResult.failed.join(', ')}</p>
            )}
          </div>
        )}
      </div>

      <h3 className="mt-6 text-sm font-medium text-text-heading dark:text-white">Pending invitations</h3>
      {pendingInvitations.length === 0 ? (
        <p className={`mt-2 ${SUBTLE}`}>No pending invitations.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {pendingInvitations.map((inv) => (
            <li
              key={inv.invitationId}
              className="flex items-center justify-between rounded-base border border-border-base dark:border-gray-700 p-2 text-sm"
            >
              <span className="text-text-body dark:text-gray-400">
                {inv.invitedEmail} · {inv.roleKey ?? '—'} · {inv.status}
              </span>
              <button
                type="button"
                aria-label={`Revoke invitation for ${inv.invitedEmail}`}
                disabled={disabled}
                onClick={() => onRevoke(inv.invitationId)}
                className="inline-flex items-center gap-1 rounded-full border border-border-base dark:border-gray-700 px-3 py-1 text-xs font-medium text-text-body dark:text-gray-400 hover:bg-error-500 hover:text-white disabled:opacity-50"
              >
                <TrashBin className="h-4 w-4" />
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3 className="mt-6 text-sm font-medium text-text-heading dark:text-white">Current participants</h3>
      {members.length === 0 ? (
        <p className={`mt-2 ${SUBTLE}`}>No participants yet.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {members.map((m) => {
            const label = m.displayName?.trim() || NEUTRAL_AUTHOR
            return (
              <li
                key={m.membershipId}
                className="flex items-center justify-between rounded-base border border-border-base dark:border-gray-700 p-2 text-sm"
              >
                <span className="text-text-body dark:text-gray-400">
                  {label} · {m.status}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  disabled={disabled}
                  onClick={() => onRemove(m.userId)}
                  className="inline-flex items-center gap-1 rounded-full border border-border-base dark:border-gray-700 px-3 py-1 text-xs font-medium text-text-body dark:text-gray-400 hover:bg-error-500 hover:text-white disabled:opacity-50"
                >
                  <TrashBin className="h-4 w-4" />
                  Remove
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

/** Small round participant avatars (initials) with a "+N" overflow. Renders
 * nothing when no members are available (graceful omission). */
function ParticipantAvatars({ members }: { members: CaseMember[] }) {
  const active = members.filter((m) => m.status !== 'REMOVED')
  if (active.length === 0) return null
  const shown = active.slice(0, 4)
  const extra = active.length - shown.length
  return (
    <div className="flex items-center -space-x-1.5" aria-label={`${active.length} participants`}>
      {shown.map((m) => {
        const label = m.displayName?.trim() || NEUTRAL_AUTHOR
        return (
          <span
            key={m.membershipId}
            title={label}
            className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-brand-100 dark:bg-gray-800 text-[10px] font-semibold text-brand-700 dark:text-brand-400"
          >
            {initialsOf(label)}
          </span>
        )
      })}
      {extra > 0 && (
        <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-background-tertiary dark:bg-gray-800 text-[10px] font-semibold text-text-body-subtle dark:text-gray-500">
          +{extra}
        </span>
      )}
    </div>
  )
}

/**
 * Shared page shell so the app header (Navbar) is present in every CaseConnect
 * state (loading, no-case, error, loaded). Mirrors OrderGuidance's standalone
 * layout: the Navbar renders in standalone launch mode inside the
 * page-container-standalone wrapper; the inner content is horizontally padded to
 * match the rest of the app. In Epic (`epic_smart`) mode the Navbar renders null
 * anyway, matching OrderGuidance's `authFlow === 'standalone'` guard.
 */
function CaseConnectShell({ children, orgName }: { children: ReactNode; orgName?: string | null }) {
  const { config } = useUiConfig()
  const { mode: launchMode } = useLaunchMode()
  const { user: auth0User, logout: auth0Logout } = useAuth0()
  const authFlow = launchMode === 'epic_smart' ? 'epic' : 'standalone'

  const handleLogout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
  }

  // Prefer the CASE's owning org name (server-resolved from the case) so the
  // header reflects the case, not the viewer's client-config org. Falls back to
  // the client-config name only when the case org name isn't available (e.g.
  // the loading/no-case states, before a case is fetched).
  const headerOrgName = (typeof orgName === 'string' && orgName.trim().length > 0 ? orgName : null) ?? getClientName(config)

  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={headerOrgName}
        />
      )}
      <div className={`flex w-full flex-col gap-4 ${authFlow === 'standalone' ? 'px-6' : ''}`}>{children}</div>
    </div>
  )
}

export default function CaseConnect() {
  const { orderId, caseId: caseIdParam } = useParams<{ orderId: string; caseId: string }>()
  const caller = useCaller()
  const { hasPermission } = useAuthorization()

  const [detail, setDetail] = useState<CaseDetail | null>(null)
  const [messages, setMessages] = useState<DiscussionMessage[]>([])
  const [suggestions, setSuggestions] = useState<CaseSuggestion[]>([])
  const [members, setMembers] = useState<CaseMember[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [voteItems, setVoteItems] = useState<CaseVoteItem[]>([])
  const [caseId, setCaseId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [noCase, setNoCase] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showAccess, setShowAccess] = useState(false)
  const [inviteResult, setInviteResult] = useState<{ sentCount: number; failed: string[] } | null>(
    null
  )

  const canDiscuss = hasPermission('case:discuss')
  const canVote = hasPermission('case:vote')
  const canSuggest = hasPermission('case:suggest')
  const canManage = hasPermission('case:manage_access')
  const canClose = hasPermission('case:close')
  // Only users who can view order info (clinician / org_admin, via result:view)
  // may jump to the order's guidance page. Case-only participants lack this, so
  // they never see the "Open case" link (preserves the isolation invariant).
  const canViewOrder = hasPermission('result:view')

  const refresh = useCallback(
    async (id: string) => {
      const [d, msgs, sugg, items] = await Promise.all([
        getCase(id, caller),
        listDiscussion(id, caller),
        listSuggestions(id, caller),
        listVoteItems(id, caller),
      ])
      setDetail(d)
      setMessages(msgs)
      setSuggestions(sugg)
      setVoteItems(items)
    },
    [caller]
  )

  /**
   * Resolve the case for this order and hydrate the page. Extracted so both the
   * mount effect and the "Create case" success path can reload the same view
   * without a hard navigation. A 404 from getCaseByOrder means no case exists
   * yet — surfaced as the calm empty state (setNoCase), not the red error.
   */
  const loadByOrder = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true)
      setError(null)
      setNoCase(false)
      try {
        const d = await getCaseByOrder(id, caller)
        const resolvedCaseId = d.case.caseId
        if (!resolvedCaseId) {
          setNoCase(true)
          return
        }
        setCaseId(resolvedCaseId)
        setDetail(d)
        setVoteItems(d.voteItems ?? [])
        const [msgs, sugg, items] = await Promise.all([
          listDiscussion(resolvedCaseId, caller),
          listSuggestions(resolvedCaseId, caller),
          listVoteItems(resolvedCaseId, caller),
        ])
        setMessages(msgs)
        setSuggestions(sugg)
        setVoteItems(items)
        // Best-effort: load participants for the header avatar cluster. Failure
        // is non-fatal (e.g. insufficient permission) — the cluster omits itself.
        void listMembers(resolvedCaseId, caller)
          .then(setMembers)
          .catch(() => setMembers([]))
      } catch (e) {
        // A 404 (typed status on CaseConnectError) means no case exists yet:
        // show the calm empty state. Any other status/network error is a real
        // failure and keeps the error state.
        if (e instanceof CaseConnectError && e.status === 404) {
          setNoCase(true)
          return
        }
        setError(e instanceof Error ? e.message : 'Failed to load case')
      } finally {
        setLoading(false)
      }
    },
    [caller]
  )

  /**
   * Load a case directly by CASE id (the org-less /cases/:caseId entry). Uses
   * the same hydration as ``refresh`` (getCase + list* by caseId) but also sets
   * ``caseId``/``detail`` so the page renders. There is no order context in this
   * mode, so the order-only affordances (Back-to-order, "Open case") are hidden
   * (they are already gated on ``orderId``/``result:view``). A failure here is a
   * real error (denied / not found -> the server 404s a case the caller may not
   * see); we never show the "create case" empty state in caseId mode.
   */
  const loadByCaseId = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true)
      setError(null)
      setNoCase(false)
      try {
        setCaseId(id)
        await refresh(id)
        // Best-effort participants for the header avatar cluster (non-fatal).
        void listMembers(id, caller)
          .then(setMembers)
          .catch(() => setMembers([]))
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load case')
      } finally {
        setLoading(false)
      }
    },
    [caller, refresh]
  )

  useEffect(() => {
    if (caseIdParam) {
      void loadByCaseId(caseIdParam)
      return
    }
    if (!orderId) return
    void loadByOrder(orderId)
  }, [orderId, caseIdParam, loadByOrder, loadByCaseId])

  if (loading) {
    return (
      <CaseConnectShell>
        <div className="p-6 text-sm font-medium text-text-body-subtle dark:text-gray-500">Loading case…</div>
      </CaseConnectShell>
    )
  }

  /**
   * Create a case for this order, then load it into the same view (no hard
   * reload). Idempotent server-side: if a case already exists, create returns
   * it and we simply load it. On failure, surface the error and leave the
   * Create button available to retry.
   */
  const handleCreate = async () => {
    if (!orderId) return
    setCreating(true)
    setCreateError(null)
    try {
      await createCaseForOrder(orderId, caller)
      await loadByOrder(orderId)
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create case. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  if (noCase && !error) {
    return (
      <CaseConnectShell>
        <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 py-6">
          <Card aria-labelledby="cc-nocase-heading">
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <h1 id="cc-nocase-heading" className={`${HEADING} text-xl`}>
                No Case Connect yet
              </h1>
              {canManage ? (
                <>
                  <p className={SUBTLE}>Start a Case Connect conference to collaborate on this order.</p>
                  <Button type="button" variant="primary" disabled={creating} onClick={() => void handleCreate()}>
                    {creating ? 'Creating…' : 'Create case'}
                  </Button>
                  {createError && (
                    <p className="text-sm font-medium text-text-fg-error dark:text-error-100" role="alert">
                      {createError}
                    </p>
                  )}
                </>
              ) : (
                <p className={SUBTLE}>No case has been started for this order yet.</p>
              )}
            </div>
          </Card>
        </main>
      </CaseConnectShell>
    )
  }

  if (error || !detail || !caseId) {
    return (
      <CaseConnectShell>
        <div className="p-6 text-sm font-medium text-text-fg-error dark:text-error-100" role="alert">
          {error ?? 'Case not found.'}
        </div>
      </CaseConnectShell>
    )
  }

  const isClosed = detail.case.status === 'CLOSED'
  const activeCaseId = caseId
  const summary = detail.case.anonymizedSummary ?? {}
  const conditionLabel = summaryConditionLabel(detail.case.anonymizedSummary)
  // Narrative paragraph under the title: prefer the first normalized condition's
  // phenotype summary, else the flat phenotype/summary/narrative key (summary-only).
  const narrative =
    (hasConditionList(summary) ? conditionString(summary.conditions[0], ['phenotypeSummary']) : null) ??
    firstStringField(summary, ['narrative', 'summary', 'phenotypeSummary', 'phenotype_summary'])
  const isInvitationOnly = (detail.case.visibility ?? 'INVITATION_ONLY') !== 'PUBLIC'

  /** Run a mutating action, refetch, and surface any failure (never a silent success).
   * Returns true on success so callers can clear inputs only after success (SHOULD-FIX #3). */
  const runAction = async (fn: () => Promise<void>): Promise<boolean> => {
    setActionError(null)
    try {
      await fn()
      await refresh(activeCaseId)
      return true
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed. Please try again.')
      return false
    }
  }

  return (
    <CaseConnectShell orgName={detail.case.orgName}>
      <main className="flex w-full flex-col gap-6 py-6">
        {/* Top action bar: Back link (left) + actions/status/participants (right). */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {orderId ? (
            <Link
              to={`/order/guidance/${orderId}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-text-body-subtle dark:text-gray-500 hover:text-brand-700 dark:hover:text-brand-400"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to Connect
            </Link>
          ) : (
            <Link
              to="/cases"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-text-body-subtle dark:text-gray-500 hover:text-brand-700 dark:hover:text-brand-400"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to cases
            </Link>
          )}
          <div className="flex flex-wrap items-center gap-3">
            {canManage && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setInviteResult(null)
                  void listMembers(activeCaseId, caller).then(setMembers)
                  // Best-effort: outstanding + accepted invitations for the
                  // pending panel. Failure is non-fatal (panel shows empty).
                  void listCaseInvitations(activeCaseId, caller)
                    .then(setInvitations)
                    .catch(() => setInvitations([]))
                  setShowAccess(true)
                }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <UserAdd className="h-4 w-4" aria-hidden />
                  Manage access
                </span>
              </Button>
            )}
            {canClose && !isClosed && (
              <Button
                type="button"
                variant="danger"
                onClick={() => void runAction(() => closeCase(activeCaseId, caller))}
              >
                Close conference
              </Button>
            )}
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                isClosed
                  ? 'bg-background-tertiary dark:bg-gray-800 text-text-body dark:text-gray-400'
                  : 'bg-brand-50 dark:bg-gray-800 text-brand-700 dark:text-brand-400'
              }`}
            >
              {isClosed ? 'Closed' : 'Active'}
            </span>
            {isInvitationOnly && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-text-body-subtle dark:text-gray-500">
                <Lock className="h-3.5 w-3.5" aria-hidden />
                Invitation only
              </span>
            )}
            <ParticipantAvatars members={members} />
          </div>
        </div>

        {/* Diagnostic/case banner: condition line, title, optional narrative paragraph. */}
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm">
          {conditionLabel && (
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-400">
              {conditionLabel}
            </p>
          )}
          <h1 className={`${HEADING} mt-1 text-2xl`}>{detail.case.title || 'Case Conference'}</h1>
          {narrative && (
            <p className="mt-3 text-sm font-medium leading-relaxed text-text-body dark:text-gray-400">{narrative}</p>
          )}
        </div>

        {actionError && (
          <div
            className="rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-3 text-sm font-medium text-text-fg-error-strong dark:text-error-100"
            role="alert"
          >
            {actionError}
          </div>
        )}

        {/* 2-column layout: main content (2 cols) + Discussion side panel (1 col); single column below lg. */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <AnonymizedReportCard detail={detail} />
            <VotingCard
              items={voteItems.filter((v) => v.source === 'GUIDANCE')}
              canVote={canVote}
              disabled={isClosed}
              onVote={(voteItemId, value) => void runAction(() => castVote(activeCaseId, voteItemId, value, caller))}
              canViewOrder={canViewOrder}
              orderId={orderId}
            />
            <SuggestionsCard
              suggestions={suggestions}
              voteItems={voteItems.filter((v) => v.source === 'SUGGESTION')}
              canSuggest={canSuggest}
              canVote={canVote}
              disabled={isClosed}
              onAdd={(body, rationale) => runAction(() => addSuggestion(activeCaseId, body, rationale || null, caller))}
              onVote={(voteItemId, value) => void runAction(() => castVote(activeCaseId, voteItemId, value, caller))}
            />
          </div>
          <div className="lg:col-span-1">
            <DiscussionPanel
              messages={messages}
              canDiscuss={canDiscuss}
              disabled={isClosed}
              onPost={(body) => runAction(() => postDiscussion(activeCaseId, body, caller))}
              onEdit={(discussionId, body) => runAction(() => editDiscussion(activeCaseId, discussionId, body, caller))}
            />
          </div>
        </div>

        {showAccess && canManage && (
          <ManageAccessModal
            members={members}
            invitations={invitations}
            disabled={isClosed}
            inviteResult={inviteResult}
            onInvite={async (emails, roleKey) => {
              setActionError(null)
              try {
                const res = await inviteMembers(activeCaseId, emails, roleKey, caller)
                const invited = res.invited ?? []
                // The flow is now pending-based: report how many invitations
                // were sent (PENDING), not "members added".
                setInviteResult({
                  sentCount: res.count ?? invited.length,
                  failed: (res as { failed?: string[] }).failed ?? [],
                })
                setMembers(await listMembers(activeCaseId, caller))
                setInvitations(await listCaseInvitations(activeCaseId, caller))
                return true
              } catch (e) {
                setActionError(e instanceof Error ? e.message : 'Invite failed. Please try again.')
                return false
              }
            }}
            onRevoke={async (invitationId) => {
              setActionError(null)
              try {
                await revokeInvitation(invitationId, caller)
                setInvitations(await listCaseInvitations(activeCaseId, caller))
              } catch (e) {
                setActionError(e instanceof Error ? e.message : 'Revoke failed. Please try again.')
              }
            }}
            onRemove={async (removedUserId) => {
              setActionError(null)
              try {
                await removeMember(activeCaseId, removedUserId, caller)
                setMembers(await listMembers(activeCaseId, caller))
              } catch (e) {
                setActionError(e instanceof Error ? e.message : 'Remove failed. Please try again.')
              }
            }}
            onClose={() => setShowAccess(false)}
          />
        )}
      </main>
    </CaseConnectShell>
  )
}

/** Route-level guard: only case:view holders may see the page. */
export function CaseConnectRoute() {
  return (
    <PermissionGuard permission="case:view" showFallback fallback={<AccessDeniedCase />}>
      <CaseConnect />
    </PermissionGuard>
  )
}

/**
 * Route-level entry for opening a case by CASE id (``/cases/:caseId``), used by
 * the org-less landing flow. Same permission guard as the order-keyed route
 * (``case:view``); the caseId mode is detected from the route param inside
 * ``CaseConnect``. Per-case authorization is still enforced SERVER-SIDE on every
 * API call (``enforce_case_in_caller_org`` = org-match OR active membership), so
 * a case the caller may not see returns the existing 404/denied — this route
 * does not weaken that. Deliberately NOT wrapped in OrderScopeGuard (there is no
 * order in scope; that probe 401s org-less users).
 */
export function CaseByIdRoute() {
  return (
    <PermissionGuard permission="case:view" showFallback fallback={<AccessDeniedCase />}>
      <CaseConnect />
    </PermissionGuard>
  )
}

function AccessDeniedCase() {
  return (
    <div className="p-6 text-sm font-medium text-text-body-subtle dark:text-gray-500" role="alert">
      You do not have access to this case.
    </div>
  )
}
